import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import type { User } from '@shared/types';
import { api } from '../services/api';

export const useAuthStore = defineStore('auth', () => {
  const user = ref<User | null>(null);
  const accessToken = ref<string | null>(null);
  const idToken = ref<string | null>(null);
  const refreshToken = ref<string | null>(null);
  const loading = ref(false);
  const error = ref<string | null>(null);

  const isAuthenticated = computed(() => !!user.value && !!idToken.value);
  const isAdmin = computed(() => user.value?.role === 'ADMIN');
  const isManager = computed(() => user.value?.role === 'MANAGER');

  function setTokens(access: string, id: string, refresh: string) {
    accessToken.value = access;
    idToken.value = id;
    refreshToken.value = refresh;
    localStorage.setItem('accessToken', access);
    localStorage.setItem('idToken', id);
    localStorage.setItem('refreshToken', refresh);
  }

  function setUser(userData: User) {
    user.value = userData;
    localStorage.setItem('user', JSON.stringify(userData));
  }

  function clearSession() {
    user.value = null;
    accessToken.value = null;
    idToken.value = null;
    refreshToken.value = null;
    localStorage.removeItem('accessToken');
    localStorage.removeItem('idToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
  }

  function restoreSession() {
    const savedAccess = localStorage.getItem('accessToken');
    const savedId = localStorage.getItem('idToken');
    const savedRefresh = localStorage.getItem('refreshToken');
    const savedUser = localStorage.getItem('user');

    if (savedAccess && savedId && savedRefresh && savedUser) {
      accessToken.value = savedAccess;
      idToken.value = savedId;
      refreshToken.value = savedRefresh;
      user.value = JSON.parse(savedUser);
    }
  }

  async function signup(email: string, password: string, managerName: string) {
    loading.value = true;
    error.value = null;

    try {
      await api.signup({ email, password, managerName });
      return true;
    } catch (err: any) {
      error.value = err.response?.data?.error?.message || 'Signup failed';
      throw err;
    } finally {
      loading.value = false;
    }
  }

  async function verify(email: string, code: string) {
    loading.value = true;
    error.value = null;

    try {
      await api.verify({ email, code });
      return true;
    } catch (err: any) {
      error.value = err.response?.data?.error?.message || 'Verification failed';
      throw err;
    } finally {
      loading.value = false;
    }
  }

  async function resendCode(email: string) {
    loading.value = true;
    error.value = null;

    try {
      await api.resendCode({ email });
      return true;
    } catch (err: any) {
      error.value = err.response?.data?.error?.message || 'Failed to resend code';
      throw err;
    } finally {
      loading.value = false;
    }
  }

  async function login(email: string, password: string) {
    loading.value = true;
    error.value = null;

    try {
      const response = await api.login({ email, password });
      setTokens(response.accessToken, response.idToken, response.refreshToken);
      setUser(response.user);
      return true;
    } catch (err: any) {
      error.value = err.response?.data?.error?.message || 'Login failed';
      throw err;
    } finally {
      loading.value = false;
    }
  }

  function logout() {
    clearSession();
  }

  return {
    user,
    accessToken,
    idToken,
    refreshToken,
    loading,
    error,
    isAuthenticated,
    isAdmin,
    isManager,
    signup,
    verify,
    resendCode,
    login,
    logout,
    restoreSession
  };
});
